import { useState } from "react";

import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons";
import { v4 as uuidV4 } from "uuid";

import { DecisionsOutcomeFilter } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import {
  informPreparingDownload,
  pollAndNotifyDownloadSuccess,
} from "src/api/downloadUtils";
import { ExporterDatasetJobsEndpoint } from "src/api/endpoints";
import { useExportDecisionsMutation } from "src/datasets/api/queries";
import { SplitButton } from "src/design-system/Button";
import { toastActions } from "src/design-system/Toast/utils";
import { useIsIcebergDatasetGenerationEnabled } from "src/hooks/useIsIcebergDatasetGenerationEnabled";
import { useFlowContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";

type DownloadFormat = "csv" | "json";

export type DownloadDetails = {
  flowSlug: string;
  flowVersionIds: string[];
  timeWindow: [string, string];
  statusCodes: string[];
  trafficPolicyId?: string;
  subSamplingSize?: number;
  outcomeFilters?: DecisionsOutcomeFilter[];
};

type Props = {
  downloadDetails?: DownloadDetails;
  disabled: boolean;
};

export const DownloadButton: React.FC<Props> = ({
  disabled,
  downloadDetails,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { flow, workspace } = useFlowContext();
  const isIcebergDatasetGenEnabled = useIsIcebergDatasetGenerationEnabled();
  const exportMutation = useExportDecisionsMutation(
    workspace.base_url,
    isIcebergDatasetGenEnabled,
  );

  const handleDownload = async (format: DownloadFormat) => {
    if (downloadDetails) {
      const toastId = uuidV4();
      try {
        setIsDownloading(true);
        informPreparingDownload(toastId);
        const downloadJob = await exportMutation.mutateAsync({
          flowId: flow.id,
          flowSlug: flow.slug,
          flowVersionIds: downloadDetails.flowVersionIds,
          startDate: downloadDetails.timeWindow[0],
          endDate: downloadDetails.timeWindow[1],
          statusCodes: downloadDetails.statusCodes,
          trafficPolicyId: downloadDetails.trafficPolicyId,
          format,
          subSamplingSize: downloadDetails.subSamplingSize,
          outcomeFilters: downloadDetails.outcomeFilters,
        });
        await pollAndNotifyDownloadSuccess(
          downloadJob,
          workspace.base_url!,
          toastId,
          ExporterDatasetJobsEndpoint.getDatasetJob,
        );
      } catch (err) {
        logger.error("Failed to create download dataset job", err);
        toastActions.failure({
          id: toastId,
          title: `Something went wrong when downloading the run results`,
        });
      } finally {
        setIsDownloading(false);
      }
    }
  };
  return (
    <SplitButton
      isDisabled={disabled}
      iconLeft={faArrowDownToLine}
      isLoading={isDownloading}
      options={[
        {
          key: "csv",
          label: "Download CSV",
          onClick: () => handleDownload("csv"),
        },
        {
          key: "json",
          label: "Download JSON",
          onClick: () => handleDownload("json"),
        },
      ]}
      variant="secondary"
    />
  );
};
